import { useState } from "react";
import { IconButton, Modal } from "rsuite";
import TrashIcon from "@rsuite/icons/Trash";
import { Button, useEditableRow, EditableRowActions, EditableTableCell, FIELD_TYPES, DataTable } from "../../../components";
import logger from "../../../utils/logger";
import ToastService from "../../../services/toast.service";
import CreateChildMatchModal from "./createChildMatchModal";
import { format } from 'date-fns-tz';
import { useAuthenticationService } from "../../../services/authentication.service";
import { roles } from "../../../utils/constants";

import "./childMatchTable.css";

// Field configuration for the child match table
const FIELD_CONFIG = {
  childId: {
    type: FIELD_TYPES.TEXT,
    regex: "\\d", // Only allow numbers
    maxLength: 10,
    placeholder: "Enter Child ID",
  },
  winzChildName: { type: FIELD_TYPES.TEXT },
  winzSwnNumber: {
    type: FIELD_TYPES.TEXT,
    regex: "\\d", // Only allow digits
    maxLength: 11, // 000-000-000 (9 digits + 2 dashes)
    placeholder: "000-000-000",
    format: (val) => {
      const numbers = val.replace(/\D/g, "").slice(0, 9);
      let formatted = "";
      for (let i = 0; i < numbers.length; i++) {
        if (i > 0 && i % 3 === 0) {
          formatted += "-";
        }
        formatted += numbers[i];
      }
      return formatted;
    },
  },
};

function ChildMatchTable({
  data,
  sortColumn,
  sortType,
  onSortColumn,
  loading,
  activeFilters,
  onFilterClick,
  // Pagination props
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  onSave,
  // Service instance passed from parent
  service,
  // Centre ID from URL
  centreId,
}) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [childMatchToDelete, setChildMatchToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const AuthenticationService = useAuthenticationService();
  const isAdmin = AuthenticationService.isInAnyRole(roles.ADMINS);

  // Use the editable row hook
  const { editingRowId, editingData, saving, startEditing, cancelEditing, handleFieldChange, saveRow, isEditing } = useEditableRow({
    onSave: handleChildSave,
    onError: (error) => {
      ToastService.error(`Failed to update child match: ${error.message || "Unknown error"}`);
    },
  });
  const handleChildCreate = async (formData) => {
    try {
      // Call the parent onSave if provided to refresh the data
      if (onSave) {
        onSave();
      }
    } catch (error) {
      logger.error("Error handling child create:", error);
    }
  };

  const handleDeleteChildMatch = (childMatch) => {
    setChildMatchToDelete(childMatch);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!childMatchToDelete) return;

    setDeleting(true);
    try {
      if (!service) {
        throw new Error("Service instance not provided");
      }
      await service.deleteChildMatch(childMatchToDelete.id);
      ToastService.success("Child match deleted successfully");
      setDeleteModalOpen(false);
      setChildMatchToDelete(null);
      if (onSave) {
        onSave(); // Refresh the data
      }
    } catch (error) {
      ToastService.error(`Failed to delete child match: ${error.message || "Unknown error"}`);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setChildMatchToDelete(null);
  };
  async function handleChildSave(id, updatedData) {
    if (!service) {
      throw new Error("Service instance not provided");
    }
    await service.updateChildMatch(id, updatedData);
    ToastService.success("Child match updated successfully");
    if (onSave) {
      onSave(); // Refresh the data
    }
  }

  const columns = [
    {
      key: "actions",
      title: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      render: (rowData) => (
        <div className="action-buttons" style={{ display: isAdmin ? "flex" : "none", gap: "4px", alignItems: "center" }}>
          <EditableRowActions
            rowData={rowData}
            isEditing={isEditing(rowData.id)}
            onEdit={startEditing}
            onSave={saveRow}
            onCancel={cancelEditing}
            saving={saving}
          />
          <IconButton
            icon={<TrashIcon />}
            size="xs"
            appearance="subtle"
            onClick={() => handleDeleteChildMatch(rowData)}
            title="Delete Child Match"
            className="delete-button"
            visible="false"
            style={{ display: isEditing(rowData.id) ? "none" : "block" }}
          />
        </div>
      ),
    },
    {
      key: "childId",
      dataKey: "childId",
      title: "Child ID",
      width: 200,
      sortable: true,
      filterable: false,
      render: (rowData) => (
        <EditableTableCell
          rowData={rowData}
          fieldKey="childId"
          fieldConfig={FIELD_CONFIG.childId}
          editingRowId={editingRowId}
          editingData={editingData}
          onFieldChange={handleFieldChange}
        />
      ),
    },
    {
      key: "winzChildName",
      dataKey: "winzChildName",
      title: "WINZ Child Name",
      flexGrow: 2,
      sortable: true,
      filterable: false,
      render: (rowData) => (
        <EditableTableCell
          rowData={rowData}
          fieldKey="winzChildName"
          fieldConfig={FIELD_CONFIG.winzChildName}
          editingRowId={editingRowId}
          editingData={editingData}
          onFieldChange={handleFieldChange}
        />
      ),
    },
    {
      key: "winzSwnNumber",
      dataKey: "winzSwnNumber",
      title: "WINZ SWN Number",
      width: 200,
      sortable: true,
      filterable: false,
      render: (rowData) => (
        <EditableTableCell
          rowData={rowData}
          fieldKey="winzSwnNumber"
          fieldConfig={FIELD_CONFIG.winzSwnNumber}
          editingRowId={editingRowId}
          editingData={editingData}
          onFieldChange={handleFieldChange}
        />
      ),
    },
    {
      key: "modifiedBy",
      dataKey: "modifiedBy",
      title: "Modified By",
      width: 150,
      sortable: true,
      filterable: true,
      render: (rowData) => (
        <span data-testid={`cell-modifiedBy-${rowData.id}`}>{rowData.modifiedBy || "N/A"}</span>
      ),
    },
    {
      key: "modifiedAt",
      dataKey: "modifiedAt",
      title: "Modified At",
      width: 200,
      sortable: true,
      filterable: true,
      render: (rowData) => (rowData.modifiedAt ? format(new Date(rowData.modifiedAt), "dd-MM-yyyy HH:mm") : "-"),
    }
    
  ];

  return (
    <>
      <div className="child-match-table-header">
        {" "}
        <Button appearance="primary" onClick={() => setShowCreateModal(true)} text="Create" disabled={!isAdmin}></Button>
      </div>
      <DataTable
        data={data}
        columns={columns}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
        loading={loading}
        activeFilters={activeFilters}
        onFilterClick={onFilterClick}
        expandable={false}
        showRowCount={true}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />{" "}
      <CreateChildMatchModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleChildCreate}
        service={service}
        centreId={centreId}
      />
      <Modal open={deleteModalOpen} onClose={cancelDelete} size="sm">
        <Modal.Header>
          <Modal.Title>Delete Child Match</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the child match for "{childMatchToDelete?.winzChildName}"?
          <br />
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={confirmDelete} appearance="primary" loading={deleting} disabled={deleting} text="Delete" color="red" data-testid="confirm-delete-button" />
          <Button onClick={cancelDelete} appearance="subtle" disabled={deleting} text="Cancel" color="blue" data-testid="cancel-delete-button" />
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ChildMatchTable;
